// src/components/exports/ImportSection.tsx
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/shared/ui/dialog";
import { Building2, Boxes } from "lucide-react";
import type { ExportScope } from "@/features/exports/services";
import { PropertyImportModal } from "@/features/properties/components/projects/PropertyImportModal";
import AssetBulkImport from "@/features/assets/components/AssetBulkImport";
import { PropertyDocumentsUploader } from "./PropertyDocumentsUploader";

interface ImportSectionProps {
  scope: ExportScope;
  /** Display name for the project — used in dialog titles, etc. */
  label?: string;
}

export function ImportSection({ scope, label }: ImportSectionProps) {
  const [propertyImportOpen, setPropertyImportOpen] = useState(false);
  const [assetImportOpen, setAssetImportOpen] = useState(false);

  if (scope.kind === "project") {
    return (
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Import</h2>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Building2 className="h-4 w-4" />
              Import Properties
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Upload a CSV to bulk-create properties under {label || "this project"}.
            </p>
            <Button onClick={() => setPropertyImportOpen(true)}>
              Open property importer
            </Button>
          </CardContent>
        </Card>

        <PropertyImportModal
          open={propertyImportOpen}
          onOpenChange={setPropertyImportOpen}
          projectId={scope.projectId}
        />
      </section>
    );
  }

  // scope.kind === "property"
  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold text-foreground">Import</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Boxes className="h-4 w-4" />
              Import Assets
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Upload a CSV to bulk-create assets under this property.
            </p>
            <Button onClick={() => setAssetImportOpen(true)}>
              Open asset importer
            </Button>
          </CardContent>
        </Card>

        <PropertyDocumentsUploader propertyId={scope.propertyId} />
      </div>

      <Dialog open={assetImportOpen} onOpenChange={setAssetImportOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Assets</DialogTitle>
            <DialogDescription>
              Bulk-create assets for {label || "this property"} from a CSV file.
            </DialogDescription>
          </DialogHeader>
          <AssetBulkImport
            propertyId={scope.propertyId}
            onComplete={() => setAssetImportOpen(false)}
          />
        </DialogContent>
      </Dialog>
    </section>
  );
}
